import React, { useState, useEffect } from 'react';
import { get } from 'aws-amplify/api';

const Members = ({ user }) => {
  const [members, setMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [filters, setFilters] = useState({
    classYear: '',
    state: '',
    search: ''
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMembers();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [members, filters]);

  const fetchMembers = async () => {
    try {
      const response = await get({
        apiName: 'navy-football-api',
        path: '/members'
      }).response;
      
      const membersData = await response.body.json();
      setMembers(membersData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching members:', error);
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...members];

    if (filters.classYear) {
      filtered = filtered.filter(member => 
        member.class_year === parseInt(filters.classYear)
      );
    }

    if (filters.state) {
      filtered = filtered.filter(member => 
        member.state?.toLowerCase() === filters.state.toLowerCase()
      );
    }

    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(member =>
        member.first_name?.toLowerCase().includes(searchTerm) ||
        member.last_name?.toLowerCase().includes(searchTerm) ||
        member.position?.toLowerCase().includes(searchTerm) ||
        member.hometown_city?.toLowerCase().includes(searchTerm)
      );
    }

    setFilteredMembers(filtered);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setFilters({ classYear: '', state: '', search: '' });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg">Loading members...</div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-md">
        <h2 className="text-2xl font-bold text-navy-primary dark:text-gold-primary mb-6">
          Members Directory
        </h2>

        {/* Filters */}
        <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Search</label>
              <input
                type="text"
                name="search"
                value={filters.search}
                onChange={handleFilterChange}
                placeholder="Name, position, hometown..."
                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Class Year</label>
              <input
                type="number"
                name="classYear"
                value={filters.classYear}
                onChange={handleFilterChange}
                placeholder="e.g., 2010"
                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">State</label>
              <input
                type="text"
                name="state"
                value={filters.state}
                onChange={handleFilterChange}
                placeholder="e.g., VA"
                maxLength="2"
                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={clearFilters}
                className="w-full bg-gray-500 text-white py-2 px-4 rounded hover:bg-gray-600 transition-colors"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {/* Results count */}
        <div className="mb-4 text-sm text-gray-600 dark:text-gray-400">
          Showing {filteredMembers.length} of {members.length} members
        </div>

        {/* Members grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredMembers.map((member, index) => (
            <div key={index} className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-12 h-12 bg-navy-primary text-white rounded-full flex items-center justify-center font-bold">
                  {member.first_name?.[0]}{member.last_name?.[0]}
                </div>
                <div>
                  <h3 className="font-semibold text-lg">
                    {member.first_name} {member.last_name}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Class of {member.class_year}
                  </p>
                </div>
              </div>
              
              <div className="space-y-2 text-sm">
                {member.position && (
                  <div>
                    <span className="font-medium">Position:</span> {member.position}
                  </div>
                )}
                {member.jersey_number && (
                  <div>
                    <span className="font-medium">Jersey:</span> #{member.jersey_number}
                  </div>
                )}
                {member.hometown_city && member.hometown_state && (
                  <div>
                    <span className="font-medium">Hometown:</span> {member.hometown_city}, {member.hometown_state}
                  </div>
                )}
                {member.service_company && (
                  <div>
                    <span className="font-medium">Service/Company:</span> {member.service_company}
                  </div>
                )}
                {member.state && (
                  <div>
                    <span className="font-medium">Current State:</span> {member.state}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {filteredMembers.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 dark:text-gray-400">
              No members found matching your criteria.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Members;